import type {RouteLocationRaw} from 'vue-router'
import {WorkspaceStatesConstant} from '@/branchmanager/app/constant/WorkspaceStatesConstant'

/**
 * Multitenancy root-screen access probes + route guard.
 *
 * When multitenancy is enabled, the branch-manager root screen (the workspace
 * dashboard) gates on two parallel, side-effect-free access checks:
 *
 *   a) the "banno me" call            -> ends with /v0/me
 *   b) the launchpad service call     -> ends with /active/service
 *
 * Outcomes:
 *   a=false, b=false -> redirect to the multitenancy login page (/login)
 *   a=true,  b=false -> redirect to the tenant picker (/institution)
 *   a=true,  b=true  -> render the workspace dashboard
 *   a=false, b=true  -> warn that /v0/me was inaccessible, render the dashboard
 *
 * These probes deliberately avoid the auto-redirect / mock-fallback behavior of
 * the existing service methods: they only answer "can we access this?".
 */

const BANNO_ME_URL = '/openanywhere/banno/v0/me'
const INSTITUTION_ROUTE_NAME = 'INSTITUTION'

async function canAccess(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
    })
    return response.ok
  } catch {
    return false
  }
}

export function canAccessBannoMe(): Promise<boolean> {
  return canAccess(BANNO_ME_URL)
}

export function canAccessActiveService(institutionId: string | null): Promise<boolean> {
  if(!institutionId) {
    return Promise.reject(new Error('institutionId is required to check access to active service'))
  }
  return canAccess(`/openanywhere/bff-employee/v1/launchpadREST/institution/${institutionId}/active/service`)
}

export function createMultitenancyRootGuard(enabled: boolean) {
  return async function multitenancyRootGuard(to): Promise<true | RouteLocationRaw> {
    if (!enabled) {
      return true
    }

    const institutionId = to.params.institutionId

    const [meResult, serviceResult] = await Promise.allSettled([canAccessBannoMe(), canAccessActiveService(institutionId)])

    const canAccessMe = meResult.status === 'fulfilled' && meResult.value
    const canAccessService = serviceResult.status === 'fulfilled' && serviceResult.value

    if (!canAccessMe && !canAccessService) {
      return {name: WorkspaceStatesConstant.LOGIN.STATE}
    }

    if (canAccessMe && !canAccessService) {
      return {name: INSTITUTION_ROUTE_NAME}
    }

    if (!canAccessMe && canAccessService) {
      console.warn(`[multitenancy] ${BANNO_ME_URL} was inaccessible; continuing to workspace dashboard`)
    }

    return true
  }
}
